import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Plus, Youtube, Tv2, Loader2, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReferenceManager from "@/components/ReferenceManager";

const EMPTY = { channel_id: null, name: "", handle: "", tone: "", style_description: "", references: [] };

export default function Channels() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/channels");
      setChannels(data);
    } catch {
      toast.error("Could not load channels");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Honor an "edit this channel" / "new channel" intent from the Dashboard.
  const location = useLocation();
  useEffect(() => {
    const id = location.state?.editChannelId;
    if (!id) return;
    if (id === "__new__") newChannel();
    else select(id);
    navigate("/channels", { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const setField = (k, v) => setCurrent((p) => ({ ...p, [k]: v }));

  const newChannel = () => setCurrent(EMPTY);

  const select = async (id) => {
    try {
      const { data } = await api.get(`/channels/${id}`);
      setCurrent({ ...EMPTY, ...data });
    } catch {
      toast.error("Could not open channel");
    }
  };

  const save = async () => {
    if (!current.name.trim()) { toast.error("Give the channel a name first"); return; }
    setSaving(true);
    try {
      const body = {
        name: current.name.trim(),
        handle: (current.handle || "").trim(),
        tone: current.tone || "",
        style_description: current.style_description || "",
        references: current.references || [],
      };
      let saved;
      if (current.channel_id) {
        ({ data: saved } = await api.put(`/channels/${current.channel_id}`, body));
      } else {
        ({ data: saved } = await api.post("/channels", body));
      }
      setCurrent({ ...EMPTY, ...saved });
      await load();
      toast.success("Channel saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!current.channel_id) return;
    try {
      await api.delete(`/channels/${current.channel_id}`);
      setConfirmDelete(false);
      newChannel();
      await load();
      toast.success("Channel deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const addReference = async (file, label, type) => {
    if ((current.references || []).length >= 10) { toast.error("Up to 10 reference images"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("label", label || "");
      form.append("ref_type", type || "character");
      const { data } = await api.post("/upload-reference", form, { headers: { "Content-Type": "multipart/form-data" } });
      setField("references", [...(current.references || []), { image_id: data.image_id, label: data.label, type: data.type }]);
      toast.success(type === "style" ? "Style reference added" : `Character "${data.label}" added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeReference = (imageId) => {
    setField("references", (current.references || []).filter((r) => r.image_id !== imageId));
  };

  return (
    <div className="grain flex h-screen overflow-hidden bg-[#0b0c10] text-[#f3f4f6]">
      {/* Left rail */}
      <aside className="flex h-full w-72 shrink-0 flex-col border-r border-[#272a35] bg-[#0e1015]">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-[#272a35]">
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-white">
            <img src={`${process.env.PUBLIC_URL}/ytf-logo.png`} alt="YouTube Factory" className="h-full w-full object-contain" />
          </div>
          <span className="font-display font-bold tracking-tight">YouTube Factory <span className="text-[#2563eb]">AI</span></span>
        </div>

        <div className="p-4 space-y-2">
          <Button
            data-testid="back-to-studio-btn"
            onClick={() => navigate("/studio")}
            variant="outline"
            className="w-full justify-start gap-2 rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Studio
          </Button>
          <Button
            data-testid="new-channel-btn"
            onClick={newChannel}
            className="w-full justify-start gap-2 rounded-md bg-[#00e5ff] text-black hover:bg-[#33ebff] font-semibold"
          >
            <Plus className="h-4 w-4" /> New channel
          </Button>
        </div>

        <div className="px-4 pb-2"><p className="overline">Your channels</p></div>

        <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#9ca3af]" /></div>
          ) : channels.length === 0 ? (
            <p className="px-2 py-6 text-sm text-[#9ca3af]">No channels yet. Create one to reuse styles &amp; characters.</p>
          ) : (
            channels.map((c) => (
              <div
                key={c.channel_id}
                data-testid={`channel-item-${c.channel_id}`}
                onClick={() => select(c.channel_id)}
                className={`group flex cursor-pointer items-center gap-2 rounded-md px-3 py-2.5 transition-colors ${
                  current.channel_id === c.channel_id ? "bg-[#1d2028] border border-[#00e5ff]/40" : "hover:bg-[#14161c] border border-transparent"
                }`}
              >
                <Tv2 className="h-4 w-4 shrink-0 text-[#9ca3af]" />
                <span className="flex-1 truncate text-sm">{c.name || "Untitled channel"}</span>
                <span className="text-xs text-[#6b7280]">{(c.references || []).length}</span>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-[#272a35] p-4">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1d2028] text-[#00e5ff]"><Youtube className="h-4 w-4" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{user?.name || "Signed in"}</p>
              <p className="truncate text-xs text-[#9ca3af]">{user?.email || ""}</p>
            </div>
            <button onClick={logout} title="Sign out" className="text-[#9ca3af] hover:text-red-400 transition-colors">
              <ArrowLeft className="h-4 w-4 rotate-180" />
            </button>
          </div>
        </div>
      </aside>

      {/* Editor */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-20 flex h-16 shrink-0 items-center justify-between border-b border-[#272a35] bg-black/60 px-5 backdrop-blur-xl">
          <div>
            <p className="overline">Channel</p>
            <h1 className="font-display text-base font-semibold tracking-tight leading-none mt-0.5">
              {current.channel_id ? (current.name || "Untitled channel") : "New channel"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {current.channel_id && (
              <Button
                data-testid="delete-channel-btn"
                onClick={() => setConfirmDelete(true)}
                variant="outline"
                className="rounded-md border-[#272a35] bg-transparent text-red-400 hover:bg-red-500/10 hover:text-red-400"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </Button>
            )}
            <Button
              data-testid="save-channel-btn"
              onClick={save}
              disabled={saving}
              className="rounded-md bg-[#00e5ff] text-black hover:bg-[#33ebff] font-semibold"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save channel
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="mx-auto max-w-2xl space-y-6">
            <div className="space-y-2">
              <Label className="text-sm text-[#9ca3af]">Channel name</Label>
              <Input
                data-testid="channel-name-input"
                value={current.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. Midnight Mysteries"
                className="bg-[#0e1015] border-[#272a35] text-[#f3f4f6] focus-visible:ring-[#00e5ff] focus-visible:ring-offset-0"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-[#9ca3af]">Channel handle</Label>
              <p className="text-xs text-[#9ca3af]">Your YouTube handle, starting with @.</p>
              <Input
                data-testid="channel-handle-input"
                value={current.handle}
                onChange={(e) => {
                  let v = e.target.value.replace(/\s+/g, "");
                  if (v && !v.startsWith("@")) v = "@" + v.replace(/@/g, "");
                  setField("handle", v);
                }}
                placeholder="@YourChannel"
                className="bg-[#0e1015] border-[#272a35] text-[#f3f4f6] focus-visible:ring-[#00e5ff] focus-visible:ring-offset-0"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-[#9ca3af]">Tone / niche</Label>
              <p className="text-xs text-[#9ca3af]">Nudges the SEO title, description &amp; scenes to match your channel voice.</p>
              <Textarea
                data-testid="channel-tone-input"
                value={current.tone}
                onChange={(e) => setField("tone", e.target.value)}
                placeholder="e.g. Suspenseful true-crime storytelling, dark and cinematic, aimed at adult mystery fans."
                className="min-h-[80px] resize-y bg-[#0e1015] border-[#272a35] text-[#f3f4f6] focus-visible:ring-[#00e5ff] focus-visible:ring-offset-0"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-[#9ca3af]">Default style description</Label>
              <p className="text-xs text-[#9ca3af]">Applied to image prompts so every video shares one look.</p>
              <Textarea
                data-testid="channel-style-input"
                value={current.style_description}
                onChange={(e) => setField("style_description", e.target.value)}
                placeholder="e.g. Moody cinematic 3D renders, teal-and-orange palette, dramatic rim lighting, filmic grain."
                className="min-h-[80px] resize-y bg-[#0e1015] border-[#272a35] text-[#f3f4f6] focus-visible:ring-[#00e5ff] focus-visible:ring-offset-0"
              />
            </div>

            <div className="border-t border-[#272a35] pt-6">
              <ReferenceManager
                references={current.references || []}
                onAdd={addReference}
                onRemove={removeReference}
                uploading={uploading}
                idPrefix="channel-"
              />
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="border-[#272a35] bg-[#14161c] text-[#f3f4f6]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this channel?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9ca3af]">
              “{current.name || "Untitled channel"}” will be removed. Stories already created from it keep their own copies.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-channel-btn"
              onClick={remove}
              className="rounded-md bg-red-500 text-white hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
